import React, { useEffect, useRef, useState } from 'react'

import type { Chart, ChartEvent, ChartType, ScriptableContext } from 'chart.js'
import { Line } from 'react-chartjs-2'

import { TDatasetMarker } from 'components_new/molecules/charts/chartjs-plugins'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import { TChartClick, TChartData, TChartVariant } from '../types'

import LoadingSection from 'components_new/molecules/LoadingSection'

import { getTheme } from 'themes'

import ChartOptionsMenu from '../ChartOptionsMenu'
import ChartWrapper from '../ChartWrapper'

// REFERENCE-LINES
import { drawHorizontalLines } from '../plugins/drawHorizontalLines'
import { drawLatestValueLabel } from '../plugins/drawLatestValueLabel'
import { drawLineOnHover } from '../plugins/drawLineOnHover'
import { drawBands } from '../plugins/drawBands'
import { drawMarkers } from '../plugins/drawMarkers'
import { indicateClickOnPointHover } from '../plugins/indicateClickOnPointHover'
import { trackCursor } from '../plugins/trackCursor'
import { EMPTY_CHART_CLICK } from '../utils/constants'
import { resolveBands, resolveMarkers } from '../utils/markers'
import {
  getChartBackgroundColorOptions,
  getChartBorderColorOptions,
  getChartBorderDashOptions,
  getChartBorderWidthOptions,
  getChartDatasetsOptions,
  getChartInteractionOptions,
  getChartLegendOptions,
  getChartPointBackgroundColorOptions,
  getChartPointColorOptions,
  getChartPointRadiusOptions,
  getChartScalesOptions,
  getChartTooltipOptions
} from '../utils/options'
import { handleOnSegmentClick } from './utils'
import { isArray } from 'lodash'
import { SxProps } from '@mui/material'

interface LineChartProps {
  beginYAtZero?: boolean
  data: TChartData
  decimals?: number
  flexItem?: boolean
  focusedMarkerId?: string
  loading?: boolean
  onMarkerClick?: (markers: TDatasetMarker[]) => void
  onSegmentClick?: (segmentIndex: number | null) => void
  options?: OptionProps[] | ((payload: TChartClick) => OptionProps[])
  significantFigures?: number
  sx?: SxProps
  variant: TChartVariant
}

const LineChart = (props: LineChartProps) => {
  const {
    beginYAtZero,
    data,
    decimals,
    flexItem,
    focusedMarkerId,
    loading,
    onMarkerClick,
    onSegmentClick,
    options: menuOptions,
    significantFigures,
    sx,
    variant
  } = props

  const theme = getTheme('light')

  const chartRef = useRef<any>(null)

  const [clicked, setClicked] = useState<TChartClick>(EMPTY_CHART_CLICK)

  useEffect(() => {
    chartRef.current?.draw()
  }, [focusedMarkerId])

  const open =
    Boolean(clicked?.position?.left !== 0) ||
    Boolean(clicked?.position?.top !== 0)

  const handleClose = () => {
    setClicked((prevState) => ({
      ...prevState,
      position: {
        left: 0,
        top: 0
      }
    }))
  }

  const handleOnClickMarker = (args: {
    chart: Chart
    event: ChartEvent
    markers: TDatasetMarker[]
  }) => {
    const { markers } = args

    if (onMarkerClick) {
      onMarkerClick(markers)
    }
  }

  return (
    <ChartWrapper flexItem={flexItem} sx={sx}>
      {loading ? (
        <LoadingSection background={true} />
      ) : (
        <>
          <Line
            data={{
              labels: data.labels,
              datasets: data.datasets.map((dataset) => {
                return {
                  backgroundColor: (ctx: ScriptableContext<ChartType>) =>
                    getChartBackgroundColorOptions({ ctx, dataset }),
                  borderColor: (ctx: ScriptableContext<ChartType>) =>
                    getChartBorderColorOptions({ ctx, dataset }),
                  borderDash: () => getChartBorderDashOptions({ dataset }),
                  borderWidth: (ctx: ScriptableContext<ChartType>) =>
                    getChartBorderWidthOptions({ ctx, dataset }),
                  data: dataset.values as (number | null)[],
                  label: dataset.label,
                  main: dataset.main,
                  markers: resolveMarkers(dataset.markers, data.periods),
                  order: dataset.order,
                  pointRadius: (ctx: ScriptableContext<ChartType>) =>
                    getChartPointRadiusOptions({ ctx, dataset, variant }),
                  pointBackgroundColor: (ctx: ScriptableContext<ChartType>) =>
                    getChartPointBackgroundColorOptions({
                      ctx,
                      dataset,
                      variant
                    }),
                  pointBorderColor: getChartPointColorOptions,
                  type: dataset.type as 'line' // actually ChartType
                }
              })
            }}
            options={{
              datasets: getChartDatasetsOptions(),
              interaction: getChartInteractionOptions(),
              maintainAspectRatio: false,
              onClick: (event, _active, chart) =>
                handleOnSegmentClick({
                  _active,
                  callback: (params: TChartClick) => {
                    if (onSegmentClick) {
                      const index = event.x
                        ? chart.scales.x.getValueForPixel(event.x)
                        : null

                      if (
                        index !== null &&
                        index !== undefined &&
                        chart.data.labels
                      ) {
                        onSegmentClick(index)
                      }
                    }
                    setClicked(params)
                  },
                  chart,
                  event
                }),
              plugins: {
                // REFERENCE-LINES
                drawHorizontalLines: {
                  decimals,
                  lines: (data.horizontalLines ?? []).map((line) => ({
                    color: line.color ?? theme.palette.action.active,
                    label: line.label,
                    value: line.value
                  })),
                  significantFigures
                },
                drawBands: {
                  bands: resolveBands(
                    data.bands,
                    data.periods,
                    theme.palette.action.selected
                  )
                },
                drawLatestValueLabel: {
                  datasets: data.isTimeseries
                    ? data.datasets.map((ds) => {
                      const color = isArray(ds.color)
                        ? ds.color[ds.color.length - 1]
                        : ds.color

                      return {
                        color: color ?? theme.palette.action.active,
                        main: ds.main
                      }
                    })
                    : [],
                  decimals,
                  significantFigures
                },
                drawLineOnHover: {
                  color:
                    variant === 'detailed'
                      ? theme.palette.action.focus
                      : undefined,
                  disabled: variant === 'minimal'
                },
                drawMarkers: {
                  focusedMarkerId,
                  onClick: handleOnClickMarker,
                  stemHeight: 20
                },
                legend: getChartLegendOptions({
                  nDatasets: data.datasets.length,
                  variant
                }),
                tooltip: getChartTooltipOptions({
                  decimals,
                  significantFigures,
                  unit: data.datasets[0].unit, // @TODO ?
                  variant
                })
              },
              responsive: true,
              scales: getChartScalesOptions({
                beginYAtZero,
                isTimeseries: data.isTimeseries,
                tickLabels: data.tickLabels,
                type: 'line',
                variant
              })
            }}
            plugins={[
              trackCursor,
              drawBands,
              drawLineOnHover,
              ...(variant === 'detailed' ? [drawLatestValueLabel] : []),
              drawHorizontalLines, // REFERENCE-LINES
              ...(variant === 'detailed' ? [drawMarkers] : []),
              ...(variant === 'detailed' ? [indicateClickOnPointHover] : [])
            ]}
            // redraw={true} // uncomment in dev
            ref={chartRef}
            style={{ width: '100%', height: '100%' }}
          />

          {menuOptions ? (
            <ChartOptionsMenu
              clicked={clicked}
              onClose={handleClose}
              open={open}
              options={menuOptions}
            />
          ) : null}
        </>
      )}
    </ChartWrapper>
  )
}

LineChart.displayName = 'LineChart'
export default LineChart
